import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import rhService, { Conge, Employe, SoldeConge, StatutConge, TypeConge } from '../../api/rh';
import { formatDate } from '../../utils/formatters';

const TYPES: TypeConge[] = ['ANNUEL', 'MALADIE', 'MATERNITE', 'PATERNITE', 'SANS_SOLDE', 'EXCEPTIONNEL'];

const STATUT_STYLE: Record<StatutConge, string> = {
  DEMANDE: 'bg-amber-100 text-amber-700',
  APPROUVE: 'bg-green-100 text-green-700',
  REFUSE: 'bg-red-100 text-red-700',
  ANNULE: 'bg-gray-100 text-gray-500',
};

const today = () => new Date().toISOString().slice(0, 10);

const Conges: React.FC = () => {
  const [employes, setEmployes] = useState<Employe[]>([]);
  const [conges, setConges] = useState<Conge[]>([]);
  const [filtreEmploye, setFiltreEmploye] = useState<string>('');
  const [solde, setSolde] = useState<SoldeConge | null>(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    employeId: '',
    typeConge: 'ANNUEL' as TypeConge,
    dateDebut: today(),
    dateFin: today(),
    nbJours: '',
    motif: '',
  });

  const refreshConges = (employeId?: string) =>
    rhService
      .listConges(employeId ? Number(employeId) : undefined)
      .then(setConges)
      .catch((e: any) => {
        if (e?.response?.status !== 403) toast.error('Chargement impossible.');
      });

  useEffect(() => {
    rhService.listEmployes(true).then(setEmployes).catch(() => {});
    refreshConges();
  }, []);

  useEffect(() => {
    refreshConges(filtreEmploye);
    if (filtreEmploye) {
      rhService.soldeConge(Number(filtreEmploye)).then(setSolde).catch(() => setSolde(null));
    } else {
      setSolde(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtreEmploye]);

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const enregistrer = async () => {
    if (!form.employeId) {
      toast.error('Sélectionnez un salarié.');
      return;
    }
    setBusy(true);
    try {
      await rhService.createConge({
        employeId: Number(form.employeId),
        typeConge: form.typeConge,
        dateDebut: form.dateDebut,
        dateFin: form.dateFin,
        nbJours: form.nbJours !== '' ? Number(form.nbJours) : undefined,
        motif: form.motif || undefined,
      });
      toast.success('Demande enregistrée.');
      setForm({ ...form, nbJours: '', motif: '' });
      refreshConges(filtreEmploye);
      if (filtreEmploye) rhService.soldeConge(Number(filtreEmploye)).then(setSolde).catch(() => {});
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Échec de l'enregistrement.");
    } finally {
      setBusy(false);
    }
  };

  const changerStatut = async (c: Conge, statut: StatutConge) => {
    try {
      await rhService.changerStatutConge(c.id, statut);
      refreshConges(filtreEmploye);
      if (filtreEmploye) rhService.soldeConge(Number(filtreEmploye)).then(setSolde).catch(() => {});
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Action impossible.');
    }
  };

  const supprimer = async (c: Conge) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    try {
      await rhService.deleteConge(c.id);
      refreshConges(filtreEmploye);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Suppression impossible.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Congés</h1>
        <p className="text-sm text-gray-500">
          Demandes et validation des congés. <Link to="/rh" className="text-blue-600 hover:underline">← Module RH</Link>
        </p>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4">
        <h2 className="text-lg font-semibold">Nouvelle demande</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Salarié</label>
            <select value={form.employeId} onChange={(e) => set('employeId', e.target.value)} className={inputCls}>
              <option value="">— Choisir —</option>
              {employes.map((e) => (
                <option key={e.id} value={e.id}>{e.nomComplet} ({e.matricule})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select value={form.typeConge} onChange={(e) => set('typeConge', e.target.value)} className={inputCls}>
              {TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Jours (auto si vide)</label>
            <input type="number" step="0.5" value={form.nbJours} onChange={(e) => set('nbJours', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Du</label>
            <input type="date" value={form.dateDebut} onChange={(e) => set('dateDebut', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Au</label>
            <input type="date" value={form.dateFin} onChange={(e) => set('dateFin', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Motif</label>
            <input value={form.motif} onChange={(e) => set('motif', e.target.value)} className={inputCls} />
          </div>
        </div>
        <button onClick={enregistrer} disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
          {busy ? 'Enregistrement…' : 'Enregistrer la demande'}
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Filtrer par salarié</label>
          <select value={filtreEmploye} onChange={(e) => setFiltreEmploye(e.target.value)} className={inputCls}>
            <option value="">Tous les salariés</option>
            {employes.map((e) => (
              <option key={e.id} value={e.id}>{e.nomComplet}</option>
            ))}
          </select>
        </div>
        {solde && (
          <div className="bg-white px-5 py-3 rounded-xl border border-gray-200 text-sm">
            <span className="text-gray-500">Solde congés {solde.annee} :</span>{' '}
            <span className="font-semibold text-gray-900">{solde.soldeRestant} j</span>{' '}
            <span className="text-gray-400">({solde.joursPris} pris / {solde.droitAnnuel} droits, {solde.joursEnAttente} en attente)</span>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3">Salarié</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Période</th>
              <th className="px-4 py-3 text-right">Jours</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {conges.map((c) => (
              <tr key={c.id} className="border-t border-gray-100">
                <td className="px-4 py-3 font-medium text-gray-900">{c.employeNom}</td>
                <td className="px-4 py-3">{c.typeConge}</td>
                <td className="px-4 py-3">{formatDate(c.dateDebut)} → {formatDate(c.dateFin)}</td>
                <td className="px-4 py-3 text-right">{c.nbJours}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${STATUT_STYLE[c.statut]}`}>{c.statut}</span>
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  {c.statut === 'DEMANDE' && (
                    <>
                      <button onClick={() => changerStatut(c, 'APPROUVE')} className="text-green-600 hover:underline">Approuver</button>
                      <button onClick={() => changerStatut(c, 'REFUSE')} className="text-red-600 hover:underline">Refuser</button>
                    </>
                  )}
                  <button onClick={() => supprimer(c)} className="text-gray-400 hover:underline">Suppr.</button>
                </td>
              </tr>
            ))}
            {conges.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-400">Aucune demande.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2';

export default Conges;
